import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import {
  Brain,
  Target,
  Users,
  TrendingUp,
  ArrowRight,
  CheckCircle,
  Sparkles,
  Rocket,
  Globe,
  Code,
  Database,
  Cpu,
  Star,
  LogIn,
  Activity,
  Compass
} from 'lucide-react';

const LandingPage = () => {
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const { scrollY } = useScroll();
  
  // Parallax effects
  const heroY = useTransform(scrollY, [0, 500], [0, -150]);
  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0]);
  const bgY = useTransform(scrollY, [0, 1000], [0, -200]);

  // Floating animation variants
  const floatingAnimation = {
    animate: {
      y: [-10, 10, -10],
      rotate: [-1, 1, -1],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const glowingOrb = {
    animate: {
      scale: [1, 1.2, 1],
      opacity: [0.5, 0.8, 0.5],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  // Features with enhanced data
  const features = [
    {
      icon: Brain,
      title: 'AI-Powered Learning',
      description: 'Neural networks analyze your learning patterns to create personalized pathways that adapt in real-time.',
      gradient: 'from-purple-500 to-pink-500',
      delay: 0.1
    },
    {
      icon: Target,
      title: 'Career-Focused',
      description: 'Industry-aligned curriculum designed by experts to match current market demands and future trends.',
      gradient: 'from-blue-500 to-cyan-500',
      delay: 0.2
    },
    {
      icon: Users,
      title: 'Collaborative Learning',
      description: 'Connect with global learners, mentors, and industry professionals in interactive learning communities.',
      gradient: 'from-green-500 to-emerald-500',
      delay: 0.3
    },
    {
      icon: TrendingUp,
      title: 'Real-time Progress',
      description: 'Advanced analytics track skill development with precision insights and predictive career mapping.',
      gradient: 'from-orange-500 to-red-500',
      delay: 0.4
    }
  ];

  const benefits = [
    'Adaptive learning paths powered by machine learning',
    'AI-driven doubt resolution with instant explanations',
    'Industry-standard project portfolio development',
    'Multi-language support with cultural localization',
    'Peer-to-peer collaboration tools and mentoring',
    'Real-world skill assessment and certification'
  ];

  // Floating elements for background
  const FloatingElement = ({ children, className, delay = 0 }) => (
    <motion.div
      className={`absolute ${className}`}
      variants={floatingAnimation}
      animate="animate"
      style={{ animationDelay: `${delay}s` }}
    >
      {children}
    </motion.div>
  );

  // Hero section component
  const HeroSection = () => {
    const isInView = useInView(heroRef, { once: true, amount: 0.3 });

    return (
      <motion.section 
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900"
        style={{ y: heroY, opacity: heroOpacity }}
      >
        {/* Animated background elements */}
        <motion.div 
          className="absolute inset-0 opacity-20"
          style={{ y: bgY }}
        >
          <FloatingElement className="top-20 left-10 text-purple-400" delay={0}>
            <Code size={40} />
          </FloatingElement>
          <FloatingElement className="top-40 right-20 text-blue-400" delay={1}>
            <Database size={35} />
          </FloatingElement>
          <FloatingElement className="bottom-40 left-20 text-pink-400" delay={2}>
            <Cpu size={45} />
          </FloatingElement>
          <FloatingElement className="bottom-20 right-10 text-green-400" delay={3}>
            <Globe size={38} />
          </FloatingElement>
        </motion.div>

        {/* Enhanced Glowing orbs */}
        <motion.div 
          className="absolute top-1/4 left-1/4 w-32 h-32 bg-purple-500 rounded-full blur-xl"
          variants={glowingOrb}
          animate="animate"
        />
        <motion.div 
          className="absolute bottom-1/4 right-1/4 w-24 h-24 bg-blue-500 rounded-full blur-xl"
          variants={glowingOrb}
          animate="animate"
          style={{ animationDelay: '1.5s' }}
        />

        {/* Main hero content */}
        <div className="relative z-10 text-center max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {/* Enhanced Logo Section */}
            <motion.div 
              className="flex items-center justify-center mb-8"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <motion.div
                className="relative"
                whileHover={{ rotateY: 360 }}
                transition={{ duration: 0.8 }}
              >
                <div className="w-24 h-24 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-2xl shadow-2xl shadow-purple-500/25 flex items-center justify-center">
                  <Brain className="w-12 h-12 text-white" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-blue-400/20 rounded-2xl animate-pulse" />
              </motion.div>
              <div className="ml-6">
                <motion.h1 
                  className="text-4xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent"
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 1, delay: 0.3 }}
                >
                  AIVIONLearn
                </motion.h1>
                <motion.p 
                  className="text-gray-400 text-lg"
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                >
                  Adaptive Learning
                </motion.p>
              </div>
            </motion.div>

            <motion.div 
              className="flex items-center justify-center mb-6"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Sparkles className="w-8 h-8 text-yellow-400 mr-3 animate-pulse" />
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent font-semibold text-lg">
                Next-Gen Learning Platform
              </span>
              <Sparkles className="w-8 h-8 text-yellow-400 ml-3 animate-pulse" />
            </motion.div>

            <motion.h1 
              className="text-6xl md:text-8xl font-bold text-white mb-8"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 1, delay: 0.2 }}
            >
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                AI-Powered
              </span>
              <br />
              <motion.span 
                className="block"
                initial={{ x: -100, opacity: 0 }}
                animate={isInView ? { x: 0, opacity: 1 } : {}}
                transition={{ duration: 1, delay: 0.4 }}
              >
                Career Evolution
              </motion.span>
            </motion.h1>

            <motion.p 
              className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Unlock your potential with <span className="text-purple-400 font-semibold">neural-adaptive</span> learning paths. 
              Master in-demand skills, build revolutionary projects, and 
              <span className="text-blue-400 font-semibold"> quantum-leap</span> your career growth.
            </motion.p>

            {/* Enhanced Action Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row items-center justify-center gap-6"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              {/* Enhanced Start Journey Button */}
              <motion.div
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 25px 50px -12px rgba(139, 92, 246, 0.5)"
                }}
                whileTap={{ scale: 0.95 }}
                className="relative group"
              >
                <Link 
                  to="/signup" 
                  className="relative inline-flex items-center px-12 py-4 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 text-white font-bold text-xl rounded-full overflow-hidden transition-all duration-500 shadow-2xl"
                  style={{ backgroundSize: '200% 100%' }}
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-purple-700 via-pink-700 to-purple-700"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: '0%' }}
                    transition={{ duration: 0.5 }}
                  />
                  <span className="relative z-10 flex items-center">
                    <motion.div
                      animate={{ rotate: [0, 360] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      className="mr-3"
                    >
                      <Rocket className="w-6 h-6" />
                    </motion.div>
                    Start Your Journey
                    <motion.div
                      className="ml-3"
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      <ArrowRight className="w-6 h-6" />
                    </motion.div>
                  </span>
                  
                  {/* Sparkle effects */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-yellow-300 rounded-full"
                        style={{
                          left: `${20 + i * 15}%`,
                          top: `${30 + i * 10}%`
                        }}
                        animate={{
                          scale: [0, 1, 0],
                          opacity: [0, 1, 0]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: i * 0.2
                        }}
                      />
                    ))}
                  </div>
                </Link>
              </motion.div>

              {/* Enhanced Login Button */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative group"
              >
                <Link 
                  to="/login" 
                  className="relative inline-flex items-center px-12 py-4 border-2 border-purple-500 text-purple-400 font-bold text-xl rounded-full hover:bg-purple-500 hover:text-white transition-all duration-500 overflow-hidden"
                >
                  <motion.div
                    className="absolute inset-0 bg-purple-500"
                    initial={{ scale: 0 }}
                    whileHover={{ scale: 1 }}
                    transition={{ duration: 0.4 }}
                    style={{ borderRadius: '50px' }}
                  />
                  <span className="relative z-10 flex items-center">
                    <motion.div
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                      className="mr-3"
                    >
                      <LogIn className="w-6 h-6" />
                    </motion.div>
                    Already a Member?
                  </span>
                </Link>
              </motion.div>
            </motion.div>

            {/* Stats section */}
            <motion.div 
              className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1 }}
            >
              {[
                { number: '10k+', label: 'Active Learners' },
                { number: '95%', label: 'Success Rate' },
                { number: '50+', label: 'Career Paths' }
              ].map((stat, index) => (
                <motion.div 
                  key={index}
                  className="text-center group cursor-pointer"
                  whileHover={{ y: -5, scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent group-hover:from-purple-400 group-hover:to-pink-400 transition-all duration-300">
                    {stat.number}
                  </div>
                  <div className="text-gray-400 mt-1 group-hover:text-gray-300 transition-colors">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse" />
          </div>
        </motion.div>
      </motion.section>
    );
  };

  // Adaptive Learning Explanation Section
  const AdaptiveLearningSection = () => {
    return (
      <motion.section className="py-24 bg-gradient-to-b from-slate-900 to-slate-800 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <motion.h2 
                className="text-5xl font-bold text-white mb-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                What is{' '}
                <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                  Adaptive Learning?
                </span>
              </motion.h2>
              
              <motion.p 
                className="text-xl text-gray-300 mb-8 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                Adaptive learning is an AI-powered educational technology that personalizes the learning experience 
                for each individual student. Our system continuously analyzes your learning patterns, strengths, and 
                areas for improvement to create a unique path tailored just for you.
              </motion.p>

              <div className="space-y-6">
                {[
                  {
                    icon: Brain,
                    title: 'Smart Content Delivery',
                    description: 'AI adjusts difficulty and pacing based on your performance'
                  },
                  {
                    icon: Activity,
                    title: 'Real-time Adaptation',
                    description: 'Learning path changes instantly as you progress and improve'
                  },
                  {
                    icon: Compass,
                    title: 'Personalized Guidance',
                    description: 'Get recommendations for what to learn next for maximum impact'
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start p-4 bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/30 hover:border-purple-500/50 transition-all duration-300"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                    whileHover={{ scale: 1.02, x: 10 }}
                  >
                    <motion.div
                      className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-4"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <item.icon className="w-6 h-6 text-white" />
                    </motion.div>
                    <div>
                      <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                      <p className="text-gray-400">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Visual */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative">
                {/* Central brain/AI visualization */}
                <motion.div 
                  className="w-80 h-80 mx-auto bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center relative overflow-hidden"
                  animate={{
                    background: [
                      'linear-gradient(45deg, #8b5cf6, #3b82f6)',
                      'linear-gradient(45deg, #3b82f6, #06b6d4)',
                      'linear-gradient(45deg, #06b6d4, #8b5cf6)'
                    ]
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <motion.div
                    className="text-white"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  >
                    <Brain size={120} />
                  </motion.div>
                  
                  {/* Orbiting elements */}
                  {[...Array(8)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-8 h-8 bg-yellow-400 rounded-full"
                      animate={{
                        rotate: 360,
                      }}
                      transition={{
                        duration: 3 + i * 0.5,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                      style={{
                        left: '50%',
                        top: '50%',
                        transformOrigin: `0 ${150 + i * 20}px`,
                        transform: `translateX(-50%) translateY(-50%) rotate(${i * 45}deg)`
                      }}
                    />
                  ))}
                </motion.div>

                {/* Connecting lines */}
                <motion.div className="absolute inset-0">
                  {[...Array(6)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-px h-20 bg-gradient-to-t from-transparent via-purple-400 to-transparent"
                      style={{
                        left: '50%',
                        top: '50%',
                        transformOrigin: '0 0',
                        transform: `rotate(${i * 60}deg) translateY(-200px)`
                      }}
                      animate={{
                        opacity: [0.3, 1, 0.3]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: i * 0.3
                      }}
                    />
                  ))}
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
    );
  };

  // Enhanced Features Section
  const FeaturesSection = () => {
    const isInView = useInView(featuresRef, { once: true, amount: 0.2 });

    return (
      <motion.section 
        ref={featuresRef}
        className="py-24 bg-gradient-to-b from-slate-800 to-slate-900 relative overflow-hidden"
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
            backgroundSize: '20px 20px'
          }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Revolutionary
              </span>
              <br />Features
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Experience the future of education with our cutting-edge AI technology 
              that adapts, evolves, and accelerates your learning journey.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="group relative"
                initial={{ opacity: 0, y: 60 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: feature.delay }}
                whileHover={{ y: -10, scale: 1.02 }}
              >
                <div className="relative h-full p-8 bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 overflow-hidden transition-all duration-300 group-hover:border-purple-500/50">
                  {/* Enhanced gradient overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                  
                  {/* Enhanced floating icon */}
                  <motion.div 
                    className={`w-16 h-16 bg-gradient-to-r ${feature.gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 relative overflow-hidden`}
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <feature.icon className="w-8 h-8 text-white relative z-10" />
                    <motion.div
                      className="absolute inset-0 bg-white/20 rounded-xl"
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0, 0.5, 0]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: index * 0.5
                      }}
                    />
                  </motion.div>

                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-purple-400 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">
                    {feature.description}
                  </p>

                  {/* Enhanced hover effect particles */}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <motion.div
                      animate={{
                        rotate: [0, 360],
                        scale: [1, 1.2, 1]
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity
                      }}
                    >
                      <Star className="w-4 h-4 text-yellow-400" />
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
    );
  };

  // Benefits Section with 3D cards
  const BenefitsSection = () => {
    return (
      <motion.section className="py-24 bg-gradient-to-b from-slate-900 to-slate-800 relative">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Why Choose <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Our Platform?</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="group flex items-center p-6 bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/30 hover:border-purple-500/50 transition-all duration-300 relative overflow-hidden"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02, x: 10 }}
              >
                <motion.div
                  className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300 relative"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <CheckCircle className="w-6 h-6 text-white" />
                  <motion.div
                    className="absolute inset-0 bg-white/20 rounded-full"
                    animate={{
                      scale: [1, 1.5, 1],
                      opacity: [0, 0.5, 0]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: index * 0.3
                    }}
                  />
                </motion.div>
                <span className="text-gray-300 group-hover:text-white transition-colors text-lg">
                  {benefit}
                </span>
                
                {/* Enhanced background effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '0%' }}
                  transition={{ duration: 0.5 }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
    );
  };

  // 🔥 Simple Working CTA Section
  const CTASection = () => {
    return (
      <motion.section className="py-24 bg-gradient-to-r from-purple-900 via-blue-900 to-purple-900 relative overflow-hidden">
        {/* Simple animated background */}
        <motion.div 
          className="absolute inset-0 opacity-20"
          animate={{ 
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            repeatType: 'reverse' 
          }}
          style={{
            backgroundImage: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
            backgroundSize: '200% 200%'
          }}
        />

        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.h2 
              className="text-5xl md:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Ready to{' '}
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                Transform
              </span>
              <br />
              Your Career?
            </motion.h2>
            
            <motion.p 
              className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Join <span className="text-yellow-400 font-bold">10,000+</span> learners who are already accelerating their careers with AI-powered learning.
              Start learning today and see results in <span className="text-green-400 font-bold">weeks, not months</span>.
            </motion.p>

            {/* Simple CTA Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <motion.div
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 25px 50px -12px rgba(251, 191, 36, 0.4)"
                }}
                whileTap={{ scale: 0.95 }}
              >
                <Link 
                  to="/signup" 
                  className="group inline-flex items-center px-12 py-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold text-xl rounded-full hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 shadow-2xl"
                >
                  <Rocket className="w-6 h-6 mr-3 group-hover:animate-bounce" />
                  Start Your Journey Today
                  <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link 
                  to="/login" 
                  className="inline-flex items-center px-12 py-4 border-2 border-purple-400 text-purple-400 font-bold text-xl rounded-full hover:bg-purple-400 hover:text-black transition-all duration-300"
                >
                  <LogIn className="w-6 h-6 mr-3" />
                  Already Learning?
                </Link>
              </motion.div>
            </motion.div>

            {/* Simple Stats */}
            <motion.div
              className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              {[
                { number: '95%', label: 'Success Rate', color: 'text-yellow-400' },
                { number: '4.9★', label: 'User Rating', color: 'text-green-400' },
                { number: '24/7', label: 'AI Support', color: 'text-blue-400' }
              ].map((stat, index) => (
                <motion.div 
                  key={index}
                  className="text-center"
                  whileHover={{ y: -5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className={`text-4xl font-bold ${stat.color} mb-2`}>
                    {stat.number}
                  </div>
                  <div className="text-gray-400">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>

            {/* Simple Benefits */}
            <motion.div
              className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 }}
            >
              {[
                '🎯 Personalized AI-driven learning paths',
                '🚀 Industry-standard project portfolio',
                '💡 Real-time doubt resolution support',
                '🤝 Connect with expert mentors globally'
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex items-center p-4 bg-slate-800/30 rounded-xl border border-slate-700/30"
                  whileHover={{ x: 10, scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <span className="text-gray-300 text-lg">{benefit}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Trust Indicator */}
            <motion.div
              className="mt-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1 }}
            >
              <div className="inline-flex items-center px-6 py-3 bg-green-500/20 rounded-full border border-green-500/30">
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-3 h-3 bg-green-400 rounded-full mr-3"
                />
                <span className="text-green-400 font-medium">Free 7-Day Trial • No Credit Card Required</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white overflow-x-hidden">
      <HeroSection />
      <AdaptiveLearningSection />
      <FeaturesSection />
      <BenefitsSection />
      <CTASection />
    </div>
  );
};

export default LandingPage;
